/**
 * useTicketNavigation - Navigate between tickets and handle return path.
 */

import { useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { z } from "zod";
import { haptic } from "@/lib/telegram";

// Session storage keys for state restoration
const SCROLL_STORAGE_KEY = "admin_ticket_view_scroll";
const EXPANDED_ORDER_STORAGE_KEY = "admin_ticket_view_expanded_order";

/**
 * Runtime validation schema for location.state.
 * React Router's location.state can be any value - validate before use.
 */
const LocationStateSchema = z.object({
  from: z.string().optional(),
  ticketIds: z.array(z.string()).optional(),
  orderId: z.string().optional(),
  scrollY: z.number().optional(),
});

type ValidatedLocationState = z.infer<typeof LocationStateSchema>;

interface UseTicketNavigationOptions {
  ticketId: string | undefined;
}

interface UseTicketNavigationResult {
  /** Navigate to previous ticket */
  handlePrev: () => void;
  /** Navigate to next ticket */
  handleNext: () => void;
  /** Close viewer and return */
  handleClose: () => void;
  /** Can navigate to previous */
  hasPrev: boolean;
  /** Can navigate to next */
  hasNext: boolean;
  /** Current ticket index (0-based) */
  currentIndex: number;
  /** Total number of tickets */
  totalTickets: number;
  /** Validated return path */
  returnPath: string;
  /** Order ID from state */
  orderId: string | undefined;
  /** All ticket IDs */
  ticketIds: string[];
  /** Saved scroll position */
  savedScrollY: number;
}

/**
 * Hook for navigating between tickets in the viewer.
 * Validates location state and provides navigation handlers.
 */
export function useTicketNavigation({
  ticketId,
}: UseTicketNavigationOptions): UseTicketNavigationResult {
  const navigate = useNavigate();
  const location = useLocation();

  // Validate location state at runtime
  const validatedState = useMemo((): ValidatedLocationState | null => {
    const result = LocationStateSchema.safeParse(location.state);
    return result.success ? result.data : null;
  }, [location.state]);

  const returnPath = validatedState?.from || "/admin/orders";
  const ticketIds = validatedState?.ticketIds || [];
  const orderId = validatedState?.orderId;
  const savedScrollY = validatedState?.scrollY ?? 0;

  // Current ticket index for navigation
  const currentIndex = ticketIds.indexOf(ticketId || "");
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex < ticketIds.length - 1;
  const totalTickets = ticketIds.length;

  // Navigate back and restore scroll position
  const handleClose = useCallback(() => {
    haptic("light");
    sessionStorage.setItem(SCROLL_STORAGE_KEY, String(savedScrollY));
    if (orderId) {
      sessionStorage.setItem(EXPANDED_ORDER_STORAGE_KEY, orderId);
    }
    navigate(returnPath, { replace: true });
  }, [navigate, returnPath, savedScrollY, orderId]);

  // Navigate to previous ticket
  const handlePrev = useCallback(() => {
    if (!hasPrev) return;
    haptic("light");
    const prevId = ticketIds[currentIndex - 1];
    navigate(`/admin/tickets/${prevId}/view`, {
      state: { from: returnPath, ticketIds, orderId, scrollY: savedScrollY },
      replace: true,
    });
  }, [hasPrev, ticketIds, currentIndex, navigate, returnPath, orderId, savedScrollY]);

  // Navigate to next ticket
  const handleNext = useCallback(() => {
    if (!hasNext) return;
    haptic("light");
    const nextId = ticketIds[currentIndex + 1];
    navigate(`/admin/tickets/${nextId}/view`, {
      state: { from: returnPath, ticketIds, orderId, scrollY: savedScrollY },
      replace: true,
    });
  }, [hasNext, ticketIds, currentIndex, navigate, returnPath, orderId, savedScrollY]);

  return {
    handlePrev,
    handleNext,
    handleClose,
    hasPrev,
    hasNext,
    currentIndex,
    totalTickets,
    returnPath,
    orderId,
    ticketIds,
    savedScrollY,
  };
}
